import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import type { AppConfig } from './config';
import { MOCK } from './mock';

let pool: CognitoUserPool | null = null;

export function initAuth(config: AppConfig): void {
  if (MOCK) return;
  pool = new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.userPoolClientId,
  });
}

const requirePool = (): CognitoUserPool => {
  if (!pool) throw new Error('auth is not initialised');
  return pool;
};

/**
 * 保存済みセッションの ID トークンを返す。期限切れなら refresh token で自動更新する。
 * 未ログイン / リフレッシュ不能なら null。
 */
export function getIdToken(): Promise<string | null> {
  if (MOCK) return Promise.resolve('mock-token');
  const user = requirePool().getCurrentUser();
  if (!user) return Promise.resolve(null);

  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export type SignInResult =
  | { status: 'signedIn' }
  /** 初回ログイン (管理者が作った仮パスワード) — 新しいパスワードの設定が必要 */
  | { status: 'newPasswordRequired'; completeWith: (newPassword: string) => Promise<void> };

export function signIn(email: string, password: string): Promise<SignInResult> {
  const user = new CognitoUser({ Username: email, Pool: requirePool() });
  const details = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: () => resolve({ status: 'signedIn' }),
      onFailure: (err) => reject(new Error(readableError(err))),
      newPasswordRequired: () => {
        resolve({
          status: 'newPasswordRequired',
          completeWith: (newPassword) =>
            new Promise<void>((done, fail) => {
              // 必須属性は admin-create-user 時に設定済みなので追加指定は不要
              user.completeNewPasswordChallenge(
                newPassword,
                {},
                {
                  onSuccess: () => done(),
                  onFailure: (err) => fail(new Error(readableError(err))),
                },
              );
            }),
        });
      },
    });
  });
}

/** パスワード再設定: 登録メールに確認コードを送る */
export function requestPasswordReset(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: requirePool() });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(new Error(readableError(err))),
    });
  });
}

/** 確認コードと新しいパスワードで再設定を完了する */
export function confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: requirePool() });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code.trim(), newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(new Error(readableError(err))),
    });
  });
}

export function signOut(): void {
  if (MOCK) return;
  requirePool().getCurrentUser()?.signOut();
}

export function currentEmail(): string | null {
  if (MOCK) return 'demo@example.com';
  return requirePool().getCurrentUser()?.getUsername() ?? null;
}

function readableError(err: unknown): string {
  const code = (err as { code?: string })?.code;
  switch (code) {
    case 'NotAuthorizedException':
      return 'メールアドレスまたはパスワードが違います';
    case 'UserNotFoundException':
      return 'ユーザーが見つかりません';
    case 'PasswordResetRequiredException':
      return 'パスワードの再設定が必要です';
    case 'InvalidPasswordException':
      return 'パスワードは12文字以上で、英小文字と数字を含めてください';
    case 'CodeMismatchException':
      return '確認コードが違います';
    case 'ExpiredCodeException':
      return '確認コードの有効期限が切れています。もう一度送信してください';
    case 'InvalidParameterException':
      // 再設定できる状態にない (仮パスワードのまま / メール未確認) ときもここに来る
      return 'このアカウントはパスワード再設定ができません。仮パスワードでログインしてください';
    case 'LimitExceededException':
      return '試行回数が多すぎます。しばらく待ってからやり直してください';
    default:
      return (err as { message?: string })?.message ?? 'ログインに失敗しました';
  }
}
